import { CssBaseline, createTheme } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFns";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { ssoApi } from "@entrilia/shared/api/sso";
import { getAuthSettings } from "@entrilia/shared/auth/settings";
import { useAuth } from "@entrilia/shared/auth/useAuth";
import AuthenticationFailed from "@entrilia/shared/components/AuthenticationFailed";
import ErrorBoundary from "@entrilia/shared/components/ErrorBoundary";
import FullScreenLoader from "@entrilia/shared/components/fullScreenLoader/FullScreenLoader";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import NoAccessPage from "@entrilia/shared/components/NoAccessPage";
import { NotificationContextProvider } from "@entrilia/shared/contexts/NotificationContextProvider";
import { logError } from "@entrilia/shared/logging";
import { darkThemeOptions, lightThemeOptions } from "@entrilia/shared/theme";
import { getUserLocaleInfo } from "@entrilia/shared/utilities/userLocaleConfig";
import { api } from "./api/operationsApi";
import type { UserInfo, UserPermissions } from "./api/userTypes";
import { redirectToSignin } from "./auth/authRedirects";
import { UserContextProvider } from "./context/UserContext";
import Main from "./Main";
import Pages from "./Pages";
import { opsPermissions, pageRoutes } from "./routes";

const getIdentity = withErrorHandling(api.identity.getIdentity);
const getUserPermissions = withErrorHandling(api.identity.getUserPermissions);

const theme = createTheme({ colorSchemes: { dark: darkThemeOptions, light: lightThemeOptions } });

const App = () => {
  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [userPermissions, setUserPermissions] = useState<UserPermissions>();
  const [authError, setAuthError] = useState<"unauthorized" | "forbidden">();

  const onAuthenticated = useCallback(async () => {
    const [[userInfoResp, userInfoError], [permissionsResp, permissionsError]] = await Promise.all([
      getIdentity({ loadLargeAvatarImage: false }),
      getUserPermissions(),
    ]);

    const error = userInfoError || permissionsError;

    if (error) {
      if (error.code === 401) {
        const { userFlowSignIn, cookieTarget } = getAuthSettings();
        await ssoApi.logout({ userFlow: userFlowSignIn, cookieTarget });
        setAuthError("unauthorized");
      } else if (error.code === 403) {
        setAuthError("forbidden");
      } else {
        logError(error, "[App] onAuthenticated");
        navigate(`/${pageRoutes.error}`);
      }
      return;
    }

    setUserInfo(userInfoResp);

    setUserPermissions(permissionsResp);
  }, [navigate]);

  const isAuthenticated = useAuth(onAuthenticated, redirectToSignin);

  if (!isAuthenticated) {
    return <FullScreenLoader title="Please wait" subtitle="You are being redirected for authentication..." />;
  }

  if (authError === "unauthorized") {
    return <AuthenticationFailed />;
  }

  if (authError === "forbidden") {
    return <NoAccessPage userEmail={userInfo?.email} onSignInWithAnotherAccountClick={redirectToSignin} />;
  }

  if (userInfo === undefined || userPermissions === undefined) {
    return <InlineLoader text="Loading user data..." />;
  }

  if (!userPermissions.permissions.some((p) => opsPermissions.includes(p))) {
    return <NoAccessPage userEmail={userInfo.email} onSignInWithAnotherAccountClick={redirectToSignin} />;
  }

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme} noSsr>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getUserLocaleInfo().locale}>
          <NotificationContextProvider>
            <UserContextProvider userInfo={userInfo} permissions={userPermissions.permissions}>
              <Main>
                <Pages />
              </Main>
            </UserContextProvider>
          </NotificationContextProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
